import { useEffect, useRef, useState } from 'react';
import { getHighRewardTier, HIGH_REWARD_THRESHOLD, HIGH_REWARD_TIER, isHighRewardPoint } from './rewardConfig';

const PUNCH_AD_INTERVAL = 20;
const FREE_PUNCH_DAILY_CHARGE = 3;
const FREE_PUNCH_COOLDOWN_SEC = 30;
const BURNING_DAILY_CHARGE = 6;
const BURNING_DURATION_SEC = 30;
const BURNING_COOLDOWN_SEC = 30;
const BURNING_COOLDOWN_START_USE_INDEX = 4;
const BURNING_START_EFFECT_DURATION_MS = 1640;
const TEN_HIT_GOLD_EFFECT_DURATION_MS = 900;
const COMBO_IDLE_RESET_MS = 2000;
const PUNCH_MOCK_STORAGE_KEY = 'punch-mock-state-v2';
const KST_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const DEFAULT_REWARD_TABLE = [
  { point: 1, weight: 16.46 },
  { point: 2, weight: 37.56 },
  { point: 3, weight: 14.28 },
  { point: 5, weight: 9.94 },
  { point: 8, weight: 10.01 },
  { point: 10, weight: 6.73 },
  { point: 20, weight: 2.16 },
  { point: 50, weight: 1.29 },
  { point: 80, weight: 0.71 },
  { point: 100, weight: 0.5 },
  { point: 150, weight: 0.36 },
];

const BURNING_REWARD_TABLE = [
  { point: 1, weight: 56.55 },
  { point: 2, weight: 19.53 },
  { point: 3, weight: 7.43 },
  { point: 5, weight: 5.17 },
  { point: 8, weight: 5.21 },
  { point: 10, weight: 3.5 },
  { point: 20, weight: 1.12 },
  { point: 50, weight: 0.67 },
  { point: 80, weight: 0.37 },
  { point: 100, weight: 0.26 },
  { point: 150, weight: 0.19 },
];

const FREE_PUNCH_REWARD_TABLE = DEFAULT_REWARD_TABLE;

const MILESTONE_META_BY_HIT = Object.freeze({
  25: { text: '워밍업!', color: '#60a5fa', emoji: '💪' },
  50: { text: '불붙었다!', color: '#34d399', emoji: '🔥' },
  75: { text: '미쳤다!', color: '#fbbf24', emoji: '⚡' },
  100: { text: '폭주 모드!', color: '#f87171', emoji: '💥' },
  125: { text: '차원이 다르다!', color: '#22d3ee', emoji: '🌊' },
  150: { text: '신의 주먹!', color: '#fbbf24', emoji: '⭐' },
  175: { text: '전설이다!', color: '#e879f9', emoji: '💎' },
  200: { text: '200 HIT 돌파!', color: '#ff0000', emoji: '🏆' },
  225: { text: '한계를 넘어!', color: '#14b8a6', emoji: '🚀' },
  250: { text: '불사신!', color: '#f59e0b', emoji: '🐉' },
  275: { text: '우주급 펀치!', color: '#8b5cf6', emoji: '🌌' },
  300: { text: '파괴신 강림!', color: '#ef4444', emoji: '💀' },
  325: { text: '시공간 붕괴!', color: '#06b6d4', emoji: '🌀' },
  350: { text: '전설의 주먹!', color: '#ec4899', emoji: '💫' },
  375: { text: '거의 다 왔다!!', color: '#eab308', emoji: '⚡' },
  400: { text: 'ULTIMATE MAX!', color: '#ff0000', emoji: '👑' },
});

const BURNING_HIGH_REWARD_TOAST_BY_POINT = Object.freeze({
  [HIGH_REWARD_THRESHOLD.BASIC]: {
    text: '오 럭키~ 50P!',
    color: '#37C98B',
  },
  [HIGH_REWARD_THRESHOLD.NICE]: {
    text: '잭팟, 나이스 80P!',
    color: '#D8A320',
  },
  [HIGH_REWARD_THRESHOLD.GREAT]: {
    text: '잭팟, 대박이야 100P!',
    color: '#B07CFF',
  },
  [HIGH_REWARD_THRESHOLD.JACKPOT]: {
    text: '완전 초대박 150P!!',
    color: '#FF4A4A',
  },
});

const PUNCH_STATE = Object.freeze({
  IDLE: 'idle',
  PUNCHING: 'punching',
});

const BURNING_STATE = Object.freeze({
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  COOLDOWN: 'cooldown',
});

const AD_STATE = Object.freeze({
  NONE: 'none',
  REQUIRED: 'required',
  REWARD: 'reward',
});

export const POPUP_TYPE = Object.freeze({
  AD_REST: 'AD_REST',
  HIGH_REWARD: 'HIGH_REWARD',
  POINT_SHORTAGE: 'POINT_SHORTAGE',
  FREE_PUNCH_AD: 'FREE_PUNCH_AD',
  BURNING_START_AD: 'BURNING_START_AD',
  BURNING_END_AD: 'BURNING_END_AD',
});

const isAdRestPunch = (punchCount) => punchCount > 0 && punchCount % PUNCH_AD_INTERVAL === 0;

const getWeightedRewardPoint = (table) => {
  const totalWeight = table.reduce((sum, item) => sum + item.weight, 0);
  const pivot = Math.random() * totalWeight;
  let acc = 0;
  for (let index = 0; index < table.length; index += 1) {
    acc += table[index].weight;
    if (pivot <= acc) return table[index].point;
  }
  return table[table.length - 1].point;
};

const getRewardPointByMode = ({ isFreePunch, isBurning }) => {
  if (isFreePunch) {
    return getWeightedRewardPoint(FREE_PUNCH_REWARD_TABLE);
  }
  if (isBurning) {
    return getWeightedRewardPoint(BURNING_REWARD_TABLE);
  }
  return getWeightedRewardPoint(DEFAULT_REWARD_TABLE);
};

const getTodayKey = () => {
  const parts = KST_DATE_FORMATTER.formatToParts(new Date());
  const y = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const m = parts.find((part) => part.type === 'month')?.value ?? '00';
  const d = parts.find((part) => part.type === 'day')?.value ?? '00';
  return `${y}-${m}-${d}`;
};

const parseServerDateTimeToMs = (value) => {
  if (!value || typeof value !== 'string') return NaN;
  const normalized = value.trim();
  if (!normalized) return NaN;

  // Preferred: normalize "YYYY-MM-DD HH:mm:ss(.S)" -> "YYYY-MM-DDTHH:mm:ss(.S)"
  const isoLike = normalized.replace(' ', 'T');
  const directParsed = new Date(isoLike).getTime();
  if (!Number.isNaN(directParsed)) return directParsed;

  // Fallback for strict parsers (e.g., iOS WebView)
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/,
  );
  if (!match) return NaN;
  const [, y, m, d, hh, mm, ss, msRaw = '0'] = match;
  const ms = Number(msRaw.padEnd(3, '0'));
  return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms).getTime();
};

export default function usePunchGame(serverPoint, options = {}) {
  const { onPunchPlay, initState, onPunchResult } = options;
  const [punchState, setPunchState] = useState({
    punch: PUNCH_STATE.IDLE,
    burning: BURNING_STATE.INACTIVE,
    ad: AD_STATE.NONE,
  });
  const [currentPoint, setCurrentPoint] = useState(0);
  const [punchCount, setPunchCount] = useState(0);
  const [pendingRewardPoint, setPendingRewardPoint] = useState(0);
  const [pendingAdRestWithHighReward, setPendingAdRestWithHighReward] = useState(false);
  const [popupQueue, setPopupQueue] = useState([]);

  const [freePunchRemain, setFreePunchRemain] = useState(FREE_PUNCH_DAILY_CHARGE);
  const [freePunchDailyMax, setFreePunchDailyMax] = useState(FREE_PUNCH_DAILY_CHARGE);
  const [freePunchBonusRemain, setFreePunchBonusRemain] = useState(1);
  const [preparedFreePunchCount, setPreparedFreePunchCount] = useState(0);
  const [freePunchCooldownEndMs, setFreePunchCooldownEndMs] = useState(0);
  const [freePunchCooldownSec, setFreePunchCooldownSec] = useState(0);

  const [burningRemain, setBurningRemain] = useState(BURNING_DAILY_CHARGE);
  const [burningDailyMax, setBurningDailyMax] = useState(BURNING_DAILY_CHARGE);
  const [burningUsedTodayCount, setBurningUsedTodayCount] = useState(0);
  const [burningDurationEndMs, setBurningDurationEndMs] = useState(0);
  const [burningDurationSec, setBurningDurationSec] = useState(0);
  const [burningPausedSec, setBurningPausedSec] = useState(0);
  const [burningCooldownEndMs, setBurningCooldownEndMs] = useState(0);
  const [burningCooldownSec, setBurningCooldownSec] = useState(0);
  const [burningHitCount, setBurningHitCount] = useState(0);
  const [burningMilestoneEvent, setBurningMilestoneEvent] = useState(null);
  const [burningStartEffectEvent, setBurningStartEffectEvent] = useState(null);
  const [burningTenHitEvent, setBurningTenHitEvent] = useState(null);

  const [pointShortageAdUsedDate, setPointShortageAdUsedDate] = useState('');
  const [floatingToastEvent, setFloatingToastEvent] = useState(null);
  const [bottomToastEvents, setBottomToastEvents] = useState([]);
  const [rewardBurstEvent, setRewardBurstEvent] = useState(null);

  const punchTimerRef = useRef(null);
  const comboResetTimerRef = useRef(null);
  const bottomToastTimersRef = useRef(new Map());
  const burningStartActivateTimerRef = useRef(null);
  const onPunchPlayRef = useRef(onPunchPlay);
  const punchLockRef = useRef(false);
  const preparedFreePunchCountRef = useRef(0);
  const freePunchPendingCompletionRef = useRef(false);

  const isPunching = punchState.punch === PUNCH_STATE.PUNCHING;
  const isBurningActive = punchState.burning === BURNING_STATE.ACTIVE;
  const activePopup = popupQueue[0] ?? null;

  const emitFloatingToast = (text) => {
    setFloatingToastEvent({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      text,
    });
  };
  const emitBottomToast = (text, color = null) => {
    const toastId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setBottomToastEvents((prev) => [
      ...prev,
      {
        id: toastId,
        text,
        color,
      },
    ]);

    const timer = window.setTimeout(() => {
      setBottomToastEvents((prev) => prev.filter((event) => event.id !== toastId));
      bottomToastTimersRef.current.delete(toastId);
    }, 2800);
    bottomToastTimersRef.current.set(toastId, timer);
  };

  const restartComboResetTimer = () => {
    if (comboResetTimerRef.current) {
      window.clearTimeout(comboResetTimerRef.current);
      comboResetTimerRef.current = null;
    }
    comboResetTimerRef.current = window.setTimeout(() => {
      setBurningHitCount(0);
      comboResetTimerRef.current = null;
    }, COMBO_IDLE_RESET_MS);
  };

  useEffect(() => {
    onPunchPlayRef.current = onPunchPlay;
  }, [onPunchPlay]);

  useEffect(() => {
    preparedFreePunchCountRef.current = preparedFreePunchCount;
  }, [preparedFreePunchCount]);

  useEffect(() => {
    if (typeof serverPoint !== 'number') return;
    if (!onPunchPlayRef.current) return;
    setCurrentPoint(serverPoint);
  }, [serverPoint]);

  useEffect(() => {
    if (onPunchPlayRef.current) return;

    const todayKey = getTodayKey();
    try {
      const raw = window.localStorage.getItem(PUNCH_MOCK_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);

      if (parsed?.dateKey === todayKey) {
        if (typeof parsed?.freePunchRemain === 'number') {
          setFreePunchRemain(parsed.freePunchRemain);
        }
        if (typeof parsed?.preparedFreePunchCount === 'number') {
          setPreparedFreePunchCount(parsed.preparedFreePunchCount);
        }
        if (typeof parsed?.pointShortageAdUsedDate === 'string') {
          setPointShortageAdUsedDate(parsed.pointShortageAdUsedDate);
        }
        if (typeof parsed?.burningRemain === 'number') {
          setBurningRemain(parsed.burningRemain);
        }
        if (typeof parsed?.burningUsedTodayCount === 'number') {
          setBurningUsedTodayCount(parsed.burningUsedTodayCount);
        }
        if (typeof parsed?.burningCooldownEndMs === 'number') {
          setBurningCooldownEndMs(parsed.burningCooldownEndMs);
        }
      } else {
        setFreePunchRemain(FREE_PUNCH_DAILY_CHARGE);
        setPointShortageAdUsedDate('');
        setBurningRemain(BURNING_DAILY_CHARGE);
        setBurningUsedTodayCount(0);
        setBurningCooldownEndMs(0);
      }
    } catch (error) {
      setFreePunchRemain(FREE_PUNCH_DAILY_CHARGE);
      setPointShortageAdUsedDate('');
      setBurningRemain(BURNING_DAILY_CHARGE);
      setBurningUsedTodayCount(0);
      setBurningCooldownEndMs(0);
    }
  }, []);

  useEffect(() => {
    if (onPunchPlayRef.current) return;

    const todayKey = getTodayKey();
    const payload = {
      dateKey: todayKey,
      freePunchRemain,
      preparedFreePunchCount,
      pointShortageAdUsedDate,
      burningRemain,
      burningUsedTodayCount,
      burningCooldownEndMs,
    };
    window.localStorage.setItem(PUNCH_MOCK_STORAGE_KEY, JSON.stringify(payload));
  }, [
    freePunchRemain,
    preparedFreePunchCount,
    pointShortageAdUsedDate,
    burningRemain,
    burningUsedTodayCount,
    burningCooldownEndMs,
  ]);

  useEffect(() => {
    if (!initState) return;

    const state = initState?.state ?? {};
    const nextBurningDailyMax =
      typeof state.burningDailyMax === 'number' && state.burningDailyMax > 0 ? state.burningDailyMax : BURNING_DAILY_CHARGE;
    const nextFreePunchDailyMax =
      typeof state.freePunchDailyMax === 'number' && state.freePunchDailyMax > 0
        ? state.freePunchDailyMax
        : FREE_PUNCH_DAILY_CHARGE;
    setBurningDailyMax(nextBurningDailyMax);
    setFreePunchDailyMax(nextFreePunchDailyMax);

    const burningDailyLeft =
      typeof state.burningDailyLeft === 'number'
        ? Math.max(0, Math.min(nextBurningDailyMax, state.burningDailyLeft))
        : null;
    const burningDailyUsed =
      typeof state.burningDailyUsed === 'number'
        ? Math.max(0, Math.min(nextBurningDailyMax, state.burningDailyUsed))
        : burningDailyLeft !== null
          ? Math.max(0, nextBurningDailyMax - burningDailyLeft)
          : 0;
    setBurningRemain(burningDailyLeft !== null ? burningDailyLeft : Math.max(0, nextBurningDailyMax - burningDailyUsed));
    setBurningUsedTodayCount(burningDailyUsed);

    if (state.burningCooldownEnd) {
      const cooldownEndMs = parseServerDateTimeToMs(state.burningCooldownEnd);
      if (cooldownEndMs > Date.now()) {
        setBurningCooldownEndMs(cooldownEndMs);
      }
    }

    if (typeof state.freePunchDailyLeft === 'number') {
      setFreePunchRemain(Math.max(0, Math.min(nextFreePunchDailyMax, state.freePunchDailyLeft)));
    }
    if (typeof state.freePunchBonusLeft === 'number') {
      setFreePunchBonusRemain(Math.max(0, Math.min(1, state.freePunchBonusLeft)));
    }

    if (preparedFreePunchCountRef.current > 0 || freePunchPendingCompletionRef.current) {
      setFreePunchCooldownEndMs(0);
      setFreePunchCooldownSec(0);
    } else if (state.freePunchCooldownEnd) {
      const freeCooldownEndMs = parseServerDateTimeToMs(state.freePunchCooldownEnd);
      if (freeCooldownEndMs > Date.now()) {
        setFreePunchCooldownEndMs(freeCooldownEndMs);
      } else {
        setFreePunchCooldownEndMs(0);
      }
    } else {
      setFreePunchCooldownEndMs(0);
    }

    if (typeof state.adPunchCounter === 'number') {
      setPunchCount(state.adPunchCounter);
    }

    if (state.burningActive === 1 && state.burningEndAt) {
      const burningEndMs = parseServerDateTimeToMs(state.burningEndAt);
      if (burningEndMs > Date.now()) {
        setBurningDurationEndMs(burningEndMs);
        setPunchState((prev) => ({ ...prev, burning: BURNING_STATE.ACTIVE }));
      }
    } else if (state.burningActive === 0) {
      if (burningStartActivateTimerRef.current) {
        window.clearTimeout(burningStartActivateTimerRef.current);
        burningStartActivateTimerRef.current = null;
      }
      setBurningDurationEndMs(0);
      setBurningDurationSec(0);
      setBurningPausedSec(0);
      setBurningHitCount(0);
      setPunchState((prev) => ({ ...prev, burning: BURNING_STATE.INACTIVE }));
    }
  }, [initState]);

  useEffect(() => {
    if (!freePunchCooldownEndMs) {
      setFreePunchCooldownSec(0);
      return;
    }

    const updateCooldown = () => {
      const remainsMs = Math.max(0, freePunchCooldownEndMs - Date.now());
      const remainsSec = Math.ceil(remainsMs / 1000);
      setFreePunchCooldownSec(remainsSec);
      if (remainsSec <= 0) {
        setFreePunchCooldownEndMs(0);
      }
    };

    updateCooldown();
    const timer = window.setInterval(updateCooldown, 250);
    return () => window.clearInterval(timer);
  }, [freePunchCooldownEndMs]);

  useEffect(() => {
    if (!burningCooldownEndMs) {
      setBurningCooldownSec(0);
      setPunchState((prev) => ({
        ...prev,
        burning: prev.burning === BURNING_STATE.ACTIVE ? prev.burning : BURNING_STATE.INACTIVE,
      }));
      return;
    }

    const updateBurningCooldown = () => {
      const remainsMs = Math.max(0, burningCooldownEndMs - Date.now());
      const remainsSec = Math.ceil(remainsMs / 1000);
      setBurningCooldownSec(remainsSec);
      if (remainsSec <= 0) {
        setBurningCooldownEndMs(0);
      } else {
        setPunchState((prev) => ({
          ...prev,
          burning: prev.burning === BURNING_STATE.ACTIVE ? prev.burning : BURNING_STATE.COOLDOWN,
        }));
      }
    };

    updateBurningCooldown();
    const timer = window.setInterval(updateBurningCooldown, 250);
    return () => window.clearInterval(timer);
  }, [burningCooldownEndMs]);

  useEffect(() => {
    if (!burningDurationEndMs) {
      setBurningDurationSec(0);
      return;
    }

    const updateBurningDuration = () => {
      const remainsMs = Math.max(0, burningDurationEndMs - Date.now());
      const remainsSec = Math.ceil(remainsMs / 1000);
      setBurningDurationSec(remainsSec);

      if (remainsSec <= 0) {
        const finalHitCount = burningHitCount;
        const shouldApplyCooldown = burningUsedTodayCount >= BURNING_COOLDOWN_START_USE_INDEX;


        setBurningDurationEndMs(0);
        setBurningPausedSec(0);
        setBurningHitCount(0);

        if (comboResetTimerRef.current) {
          window.clearTimeout(comboResetTimerRef.current);
          comboResetTimerRef.current = null;
        }

        if (shouldApplyCooldown) {
          setBurningCooldownEndMs(Date.now() + BURNING_COOLDOWN_SEC * 1000);
          setPunchState((prev) => ({ ...prev, burning: BURNING_STATE.COOLDOWN }));
          emitBottomToast(`버닝타임 종료! 🔥${finalHitCount} Hit! ⏳ 쿨타임 30초`);
        } else {
          setPunchState((prev) => ({ ...prev, burning: BURNING_STATE.INACTIVE }));
          emitBottomToast(`버닝타임 종료! 🔥${finalHitCount} Hit!`);
        }

        setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.BURNING_END_AD, hitCount: finalHitCount }]);
      }
    };

    updateBurningDuration();
    const timer = window.setInterval(updateBurningDuration, 250);
    return () => window.clearInterval(timer);
  }, [burningDurationEndMs, burningHitCount, burningUsedTodayCount]);

  useEffect(() => {
    if (!floatingToastEvent?.id) return undefined;
    const currentId = floatingToastEvent.id;
    const timer = window.setTimeout(() => {
      setFloatingToastEvent((prev) => (prev?.id === currentId ? null : prev));
    }, 2100);
    return () => window.clearTimeout(timer);
  }, [floatingToastEvent]);

  useEffect(() => {
    if (!burningMilestoneEvent?.id) return undefined;
    const currentId = burningMilestoneEvent.id;
    const timer = window.setTimeout(() => {
      setBurningMilestoneEvent((prev) => (prev?.id === currentId ? null : prev));
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [burningMilestoneEvent]);

  useEffect(() => {
    if (!burningStartEffectEvent?.id) return undefined;
    const currentId = burningStartEffectEvent.id;
    const timer = window.setTimeout(() => {
      setBurningStartEffectEvent((prev) => (prev?.id === currentId ? null : prev));
    }, BURNING_START_EFFECT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [burningStartEffectEvent]);

  useEffect(() => {
    if (!burningTenHitEvent?.id) return undefined;
    const currentId = burningTenHitEvent.id;
    const timer = window.setTimeout(() => {
      setBurningTenHitEvent((prev) => (prev?.id === currentId ? null : prev));
    }, TEN_HIT_GOLD_EFFECT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [burningTenHitEvent]);

  useEffect(() => {
    const bottomToastTimers = bottomToastTimersRef.current;
    return () => {
      if (punchTimerRef.current) {
        clearTimeout(punchTimerRef.current);
      }
      if (comboResetTimerRef.current) {
        window.clearTimeout(comboResetTimerRef.current);
      }
      bottomToastTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      bottomToastTimers.clear();
      if (burningStartActivateTimerRef.current) {
        window.clearTimeout(burningStartActivateTimerRef.current);
      }
      punchLockRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!activePopup) {
      setPunchState((prev) => ({ ...prev, ad: AD_STATE.NONE }));
      return;
    }

    if (
      activePopup.type === POPUP_TYPE.AD_REST ||
      activePopup.type === POPUP_TYPE.FREE_PUNCH_AD ||
      activePopup.type === POPUP_TYPE.BURNING_START_AD
    ) {
      setPunchState((prev) => ({ ...prev, ad: AD_STATE.REQUIRED }));
      return;
    }

    if (activePopup.type === POPUP_TYPE.HIGH_REWARD || activePopup.type === POPUP_TYPE.BURNING_END_AD) {
      setPunchState((prev) => ({ ...prev, ad: AD_STATE.REWARD }));
      return;
    }

    setPunchState((prev) => ({ ...prev, ad: AD_STATE.NONE }));
  }, [activePopup]);

  const applyBurningHitCount = ({ requiresHighRewardAd, rewardTier }) => {
    setBurningHitCount((prev) => {
      let nextHit = prev + 1;

      if (nextHit % 10 === 0 && nextHit < 400) {
        setBurningTenHitEvent({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          hit: nextHit,
        });
      }

      if (nextHit % 25 === 0) {
        const milestoneMeta = MILESTONE_META_BY_HIT[nextHit] ?? {
          text: `${nextHit} HIT!`,
          color: '#000000',
          emoji: '🔥',
        };
        setBurningMilestoneEvent({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          hit: nextHit,
          text: milestoneMeta.text,
        });
        const milestonePrefix = milestoneMeta.emoji ? `${milestoneMeta.emoji} ` : '';
        emitBottomToast(`${milestonePrefix}${nextHit} HIT! ${milestoneMeta.text}`, milestoneMeta.color);
      }

      if (nextHit >= 400) {
        setCurrentPoint((point) => point + 1);
        emitBottomToast('1 포인트 획득, 콤보 리셋!');
        nextHit = 0;
      }

      return nextHit;
    });

    if (!(requiresHighRewardAd && rewardTier === HIGH_REWARD_TIER.JACKPOT)) {
      restartComboResetTimer();
    }
  };

  const executePunch = async ({ isFreePunch = false } = {}) => {
    if (punchLockRef.current) return false;
    if (isPunching) return false;

    const burningActiveAtPunch = punchState.burning === BURNING_STATE.ACTIVE;
    const punchCost = isFreePunch ? 0 : burningActiveAtPunch ? 3 : 5;

    if (currentPoint < punchCost) return false;
    punchLockRef.current = true;

    setPunchState((prev) => ({ ...prev, punch: PUNCH_STATE.PUNCHING }));
    punchTimerRef.current = setTimeout(() => {
      setPunchState((prev) => ({ ...prev, punch: PUNCH_STATE.IDLE }));
      punchTimerRef.current = null;
      if (!onPunchPlayRef.current) {
        punchLockRef.current = false;
      }
    }, 200);

    if (onPunchPlayRef.current) {
      // 실서버 모드: API 응답값으로 잔액 한 번에 반영 (선행 차감 없음)
      const mode = isFreePunch ? 'free' : burningActiveAtPunch ? 'burning' : 'normal';
      const eventId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;


      try {
        const result = await onPunchPlayRef.current({ mode, eventId });
        if (typeof onPunchResult === 'function') {
          onPunchResult(result);
        }

        const rewardMeta = result?.rewards?.[0];
        const rewardPoint = rewardMeta?.reward ?? result?.rewardGranted;
        if (typeof rewardPoint !== 'number') {
          return false;
        }
        const requiresAd = rewardMeta?.requiresAd ?? false;
        const highReward = rewardMeta?.highReward ?? false;
        const needMandatoryAd = result?.needMandatoryAd ?? false;
        const newAdPunchCounter = result?.adPunchCounter;
        const newFreePunchDailyLeft = result?.freePunchDailyLeft;
        const newFreePunchBonusLeft = result?.freePunchBonusLeft;
        const newBalance = result?.balance;
        const costSpent = result?.costSpent ?? punchCost;
        const rewardTier = getHighRewardTier(rewardPoint);
        const shouldQueueAdRest = needMandatoryAd && !burningActiveAtPunch;

        // 서버 잔액으로 단일 업데이트 (깜빡임 없음)
        if (typeof newBalance === 'number') {
          setCurrentPoint(newBalance);
        } else {
          // balance 없으면 costSpent 차감 + 즉시 지급 보상 합산
          setCurrentPoint((prev) => prev - costSpent + (requiresAd ? 0 : rewardPoint));
        }

        // 광고 카운터 동기화
        if (typeof newAdPunchCounter === 'number') {
          setPunchCount(newAdPunchCounter);
        } else {
          setPunchCount((prev) => prev + 1);
        }
        if (typeof newFreePunchDailyLeft === 'number') {
          setFreePunchRemain(Math.max(0, Math.min(freePunchDailyMax, newFreePunchDailyLeft)));
        }
        if (typeof newFreePunchBonusLeft === 'number') {
          setFreePunchBonusRemain(Math.max(0, Math.min(1, newFreePunchBonusLeft)));
        }

        if (rewardPoint <= 0) {
          if (shouldQueueAdRest) {
            setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.AD_REST }]);
          }
          return true;
        }

        setRewardBurstEvent({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          rewardPoint,
          highReward,
          isBurning: burningActiveAtPunch,
        });
        emitFloatingToast(`${rewardPoint}P 획득!`);

        if (burningActiveAtPunch && comboResetTimerRef.current) {
          window.clearTimeout(comboResetTimerRef.current);
          comboResetTimerRef.current = null;
        }

        if (requiresAd) {
          setPendingRewardPoint(rewardPoint);
          setPendingAdRestWithHighReward(shouldQueueAdRest);
          if (burningActiveAtPunch && rewardTier === HIGH_REWARD_TIER.JACKPOT) {
            const remainsSec = Math.max(1, Math.ceil((burningDurationEndMs - Date.now()) / 1000));
            setBurningPausedSec(remainsSec);
            setBurningDurationEndMs(0);
            if (comboResetTimerRef.current) {
              window.clearTimeout(comboResetTimerRef.current);
              comboResetTimerRef.current = null;
            }
          }
          setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.HIGH_REWARD, rewardPoint }]);
        } else {
          if (burningActiveAtPunch && rewardTier !== HIGH_REWARD_TIER.NONE) {
            const burningHighRewardToast = BURNING_HIGH_REWARD_TOAST_BY_POINT[rewardPoint];
            if (burningHighRewardToast) {
              emitBottomToast(burningHighRewardToast.text, burningHighRewardToast.color);
            } else {
              emitBottomToast(`🔥 ${rewardPoint}P 즉시 지급!`);
            }
          }
          if (shouldQueueAdRest) {
            setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.AD_REST }]);
          }
        }

        if (burningActiveAtPunch) {
          applyBurningHitCount({ requiresHighRewardAd: requiresAd, rewardTier });
        }

      } catch (error) {
      } finally {
        punchLockRef.current = false;
      }
    } else {
      // 목업 모드: 클라이언트 로컬 계산
      const nextPunchCount = punchCount + 1;
      const rewardPoint = getRewardPointByMode({ isFreePunch, isBurning: burningActiveAtPunch });
      const highReward = isHighRewardPoint(rewardPoint);
      const rewardTier = getHighRewardTier(rewardPoint);
      const shouldTriggerAdRest = isAdRestPunch(nextPunchCount);
      const shouldRequireHighRewardAd =
        highReward && (!burningActiveAtPunch || rewardPoint === HIGH_REWARD_THRESHOLD.JACKPOT);
      const shouldQueueAdRest = shouldTriggerAdRest && !burningActiveAtPunch;

      setRewardBurstEvent({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        rewardPoint,
        highReward,
        isBurning: burningActiveAtPunch,
      });

      setPunchCount(nextPunchCount);
      setCurrentPoint((prev) => prev - punchCost);
      emitFloatingToast(`${rewardPoint}P 획득!`);

      if (burningActiveAtPunch && comboResetTimerRef.current) {
        window.clearTimeout(comboResetTimerRef.current);
        comboResetTimerRef.current = null;
      }

      if (!shouldRequireHighRewardAd) {
        setCurrentPoint((prev) => prev + rewardPoint);
        if (burningActiveAtPunch && rewardTier !== HIGH_REWARD_TIER.NONE) {
          const burningHighRewardToast = BURNING_HIGH_REWARD_TOAST_BY_POINT[rewardPoint];
          if (burningHighRewardToast) {
            emitBottomToast(burningHighRewardToast.text, burningHighRewardToast.color);
          } else {
            emitBottomToast(`🔥 ${rewardPoint}P 즉시 지급!`);
          }
        }
      } else {
        setPendingRewardPoint(rewardPoint);
        setPendingAdRestWithHighReward(shouldQueueAdRest);
        if (burningActiveAtPunch && rewardTier === HIGH_REWARD_TIER.JACKPOT) {
          const remainsSec = Math.max(1, Math.ceil((burningDurationEndMs - Date.now()) / 1000));
          setBurningPausedSec(remainsSec);
          setBurningDurationEndMs(0);
          if (comboResetTimerRef.current) {
            window.clearTimeout(comboResetTimerRef.current);
            comboResetTimerRef.current = null;
          }
        }
        setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.HIGH_REWARD, rewardPoint }]);
      }

      if (shouldQueueAdRest && !shouldRequireHighRewardAd) {
        setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.AD_REST }]);
      }

      if (burningActiveAtPunch) {
        applyBurningHitCount({ requiresHighRewardAd: shouldRequireHighRewardAd, rewardTier });
      }
    }

    return true;
  };

  const handlePunch = () => {
    if (activePopup) return;

    const hasPreparedFreePunch = preparedFreePunchCount > 0;
    const punchCost = hasPreparedFreePunch ? 0 : punchState.burning === BURNING_STATE.ACTIVE ? 3 : 5;

    if (!hasPreparedFreePunch && currentPoint < punchCost) {
      const isServerMode = !!onPunchPlayRef.current;
      const todayKey = getTodayKey();
      const adAvailable = isServerMode ? freePunchBonusRemain > 0 : pointShortageAdUsedDate !== todayKey;
      setPopupQueue((prev) => [...prev, { type: POPUP_TYPE.POINT_SHORTAGE, adAvailable }]);
      return;
    }

    if (hasPreparedFreePunch) {
      setPreparedFreePunchCount((prev) => Math.max(0, prev - 1));
      freePunchPendingCompletionRef.current = true;
      executePunch({ isFreePunch: true }).then((completed) => {
        if (!completed) {
          freePunchPendingCompletionRef.current = false;
          return;
        }

        window.setTimeout(() => {
          freePunchPendingCompletionRef.current = false;
          setFreePunchCooldownEndMs(Date.now() + FREE_PUNCH_COOLDOWN_SEC * 1000);
          emitBottomToast('무료펀치 완료!');
        }, 200);
      });
      return;
    }

    executePunch();
  };

  const handleUseFreePunch = () => {
    if (punchState.burning === BURNING_STATE.ACTIVE) {
      emitBottomToast('🔥 버닝타임 중에는 이용할 수 없어요!');
      return { ok: false, reason: 'BURNING_ACTIVE' };
    }
    if (freePunchRemain <= 0) {
      return { ok: false, reason: 'NO_REMAIN' };
    }
    if (preparedFreePunchCount > 0) {
      return { ok: false, reason: 'ALREADY_READY' };
    }
    if (freePunchCooldownSec > 0) {
      return { ok: false, reason: 'COOLDOWN', cooldownSec: freePunchCooldownSec };
    }
    return { ok: true, pendingAd: true };
  };

  const handleUseBurning = () => {
    if (punchState.burning === BURNING_STATE.ACTIVE) {
      return { ok: false, reason: 'ALREADY_ACTIVE' };
    }
    if (burningCooldownSec > 0) {
      return { ok: false, reason: 'COOLDOWN', cooldownSec: burningCooldownSec };
    }
    if (burningRemain <= 0) {
      return { ok: false, reason: 'NO_REMAIN' };
    }
    return { ok: true, pendingAd: true };
  };

  const closeCurrentPopup = () => {
    if (activePopup?.type === POPUP_TYPE.HIGH_REWARD) {
      setPendingRewardPoint(0);
      setPendingAdRestWithHighReward(false);
    }
    setPopupQueue((prev) => prev.slice(1));
  };

  const handleWatchAdForRest = () => {
    setCurrentPoint((prev) => prev + 1);
    emitBottomToast('광고 시청 완료! +1P 획득!');
    emitFloatingToast('1P 획득!');
    closeCurrentPopup();
  };

  const handleWatchAdForHighReward = () => {

    if (pendingRewardPoint > 0) {
      const pendingRewardTier = getHighRewardTier(pendingRewardPoint);
      const doubledRewardPoint = pendingRewardPoint * 2;
      const adRestBonus = pendingAdRestWithHighReward ? 1 : 0;
      setCurrentPoint((prev) => prev + pendingRewardPoint + adRestBonus);
      if (pendingAdRestWithHighReward) {
        emitBottomToast(`광고 완료! +1P + ${pendingRewardPoint}P 고보상 지급!`);
      } else if (burningPausedSec > 0 && pendingRewardTier === HIGH_REWARD_TIER.JACKPOT) {
        const jackpotToast = BURNING_HIGH_REWARD_TOAST_BY_POINT[HIGH_REWARD_THRESHOLD.JACKPOT];
        emitBottomToast(`+${pendingRewardPoint}P 추가 획득!`, jackpotToast.color);
      } else if (pendingRewardTier === HIGH_REWARD_TIER.GREAT || pendingRewardTier === HIGH_REWARD_TIER.JACKPOT) {
        emitBottomToast(`+${pendingRewardPoint}P 추가 획득!`);
      } else {
        emitBottomToast(`보상 ${pendingRewardPoint}P 지급 완료!`);
      }
    }

    if (burningPausedSec > 0) {
      setBurningDurationEndMs(Date.now() + burningPausedSec * 1000);
      setBurningPausedSec(0);
      if (burningHitCount > 0) {
        restartComboResetTimer();
      }
    }

    setPendingRewardPoint(0);
    setPendingAdRestWithHighReward(false);
    closeCurrentPopup();
  };

  const handleSkipHighReward = () => {
    if (burningPausedSec > 0) {
      setBurningDurationEndMs(Date.now() + burningPausedSec * 1000);
      setBurningPausedSec(0);
      if (burningHitCount > 0) {
        restartComboResetTimer();
      }
    }

    setPendingRewardPoint(0);
    setPendingAdRestWithHighReward(false);
    emitBottomToast('보상을 포기했습니다');
    closeCurrentPopup();
  };

  const handleWatchAdForBurningStart = (serverState = null) => {
    const hasServerState = serverState && typeof serverState === 'object';
    const serverBurningEndMs = hasServerState ? parseServerDateTimeToMs(serverState.burningEndAt) : NaN;
    const shouldUseServerBurning =
      hasServerState && serverState.burningActive === 1 && serverBurningEndMs > Date.now();

    if (hasServerState && !shouldUseServerBurning) {
      if (burningStartActivateTimerRef.current) {
        window.clearTimeout(burningStartActivateTimerRef.current);
        burningStartActivateTimerRef.current = null;
      }
      emitBottomToast('버닝타임 상태 확인에 실패했어요. 잠시 후 다시 시도해 주세요.');
      closeCurrentPopup();
      return;
    }

    setBurningRemain((prev) => Math.max(0, prev - 1));
    setBurningUsedTodayCount((prev) => prev + 1);
    setBurningDurationEndMs(0);
    setBurningDurationSec(0);
    setBurningHitCount(0);
    setBurningPausedSec(0);

    if (comboResetTimerRef.current) {
      window.clearTimeout(comboResetTimerRef.current);
      comboResetTimerRef.current = null;
    }

    setBurningStartEffectEvent({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      line1: '30초간 광고 100% 스킵',
      line2: '400 HIT에 도전하라!!',
    });
    if (burningStartActivateTimerRef.current) {
      window.clearTimeout(burningStartActivateTimerRef.current);
      burningStartActivateTimerRef.current = null;
    }
    burningStartActivateTimerRef.current = window.setTimeout(() => {
      const burningEndMs = shouldUseServerBurning ? serverBurningEndMs : Date.now() + BURNING_DURATION_SEC * 1000;
      setBurningDurationEndMs(burningEndMs);
      setBurningDurationSec(Math.max(0, Math.ceil((burningEndMs - Date.now()) / 1000)));
      setPunchState((prev) => ({ ...prev, burning: BURNING_STATE.ACTIVE }));
      emitBottomToast('🔥 버닝타임 시작! 30초간 광고 스킵!');
      burningStartActivateTimerRef.current = null;
    }, BURNING_START_EFFECT_DURATION_MS);

    closeCurrentPopup();
  };

  const handleWatchAdForBurningEnd = () => {
    setCurrentPoint((prev) => prev + 2);
    emitBottomToast('광고 시청 완료! +2P 획득!');
    emitFloatingToast('2P 획득!');
    closeCurrentPopup();
  };

  const handleSkipBurningEndAd = () => {
    closeCurrentPopup();
  };

  const handleWatchAdForPointShortage = () => {
    const todayKey = getTodayKey();
    setPointShortageAdUsedDate(todayKey);
    setFreePunchBonusRemain(0);
    setFreePunchRemain((prev) => prev + 1);
    closeCurrentPopup();
  };

  const handleChargeForPointShortage = () => {
    closeCurrentPopup();
  };

  const handleWatchAdForFreePunch = () => {
    setFreePunchRemain((prev) => Math.max(0, prev - 1));
    setPreparedFreePunchCount((prev) => prev + 1);
    setFreePunchCooldownEndMs(0);
    setFreePunchCooldownSec(0);
    emitBottomToast('🎁 무료 펀치 준비 완료! 터치하세요!');
    closeCurrentPopup();
  };

  return {
    isPunching,
    isBurningActive,
    currentPoint,
    activePopup,
    freePunchRemain,
    freePunchDailyMax,
    preparedFreePunchCount,
    freePunchCooldownSec,
    burningRemain,
    burningDailyMax,
    burningDurationSec: burningDurationSec || burningPausedSec,
    burningCooldownSec,
    burningHitCount,
    burningMilestoneEvent,
    burningStartEffectEvent,
    burningTenHitEvent,
    floatingToastEvent,
    bottomToastEvents,
    rewardBurstEvent,
    handlePunch,
    handleUseFreePunch,
    handleUseBurning,
    handleWatchAdForRest,
    handleWatchAdForHighReward,
    handleSkipHighReward,
    handleWatchAdForBurningStart,
    handleWatchAdForBurningEnd,
    handleSkipBurningEndAd,
    handleWatchAdForPointShortage,
    handleChargeForPointShortage,
    handleWatchAdForFreePunch,
    closeCurrentPopup,
  };
}
